import React, {createContext, useContext, useState} from "react";
import {getCurrentUrlMenuOption} from "../models/MenuOption";
import {useLocation, useNavigate} from "react-router-dom";
import {NavigateOptions} from "react-router-dom";
import { useSafeBackNavigation, hasModalBackground, resolveModalBackground } from "../hooks/useSafeBackNavigation";

interface MenuContextType {
    selected: string;
    setSelected: React.Dispatch<React.SetStateAction<string>>;
    setPreviousSelected: React.Dispatch<React.SetStateAction<string>>;
    navigateTo: (route: string | number, withBackground?: boolean, payload?: NavigateOptions) => void;
    navigateBack: () => void;
    isSidebarOpen: boolean;
    setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
    changeToPreviousSelected: () => void;
}

export const MenuContext = createContext<MenuContextType | null>(null);

export const MenuContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [selected, setSelected] = useState<string>(getCurrentUrlMenuOption());
    const [previousSelected, setPreviousSelected] = useState<string>(selected);
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();
    const {goBack} = useSafeBackNavigation('/');

    const navigateTo = (route: string | number, withBackground: boolean = false, payload: NavigateOptions = {}) => {
        if (
            typeof route === 'string'
            && withBackground
            && location.pathname === route
            && hasModalBackground(location)
        ) {
            return;
        }

        const pageLocation = hasModalBackground(location)
            ? resolveModalBackground(location)
            : location;

        const nextState = withBackground
            ? {
                ...(payload.state ?? {}),
                background: pageLocation,
            }
            : payload.state;

        // withBackground=true requires a matching route in the overlay Routes block
        // (see routes.tsx — second Routes when location.state.background is set).
        navigate(route as any, {
            ...payload,
            state: nextState,
        });
    };

    const changeToPreviousSelected = () => {
        setSelected(previousSelected);
    };

    const value = {
        selected, setSelected, setPreviousSelected, navigateTo, navigateBack: goBack,
        isSidebarOpen, setIsSidebarOpen, changeToPreviousSelected
    };

    return (
        <MenuContext.Provider value={value}> {children} </MenuContext.Provider>
    );
};

export default MenuContext;

export const useMenu = (): MenuContextType => {
    const ctx = useContext(MenuContext);
    if (!ctx) throw new Error('useMenu must be used within MenuContextProvider');
    return ctx;
};
